import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, InputAdornment, Stack, Typography } from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import {
  Button,
  Paper,
  SurfaceTextField,
  TabBar,
  TabBarItem,
} from '../../components/design-system';
import { Breadcrumbs, CreateContactCard } from '../../components';
import {
  BlockedUsersTable,
  ContactsTable,
  IncomingRequestsTable,
  SentRequestsTable,
} from './ContactsTables';
import {
  ContactRow,
  BlockedUserRow,
  getContactName,
  IncomingRequestRow,
  SentRequestRow,
} from './types';

export type ContactsTab = 'contacts' | 'incoming' | 'sent' | 'blocked';

interface ContactsOverviewProps {
  activeTab: ContactsTab;
  blockedUsers: BlockedUserRow[];
  contacts: ContactRow[];
  incomingRequests: IncomingRequestRow[];
  loading: boolean;
  onAccept: (id: string) => void;
  onRemoveRequest: (id: string) => void;
  sentRequests: SentRequestRow[];
}

const tabPaths: Record<ContactsTab, string> = {
  contacts: '/contacts',
  incoming: '/contacts/requests',
  sent: '/contacts/sent',
  blocked: '/contacts/blocked',
};

const headerStyles = {
  alignItems: { xs: 'flex-start', sm: 'center' },
  justifyContent: 'space-between',
};
const headingStyles = { fontWeight: 700 };
const descriptionStyles = { color: 'text.secondary' };
const searchStyles = { width: { xs: '100%', sm: 320 } };
const emptyStyles = { p: 4, textAlign: 'center' };
const startAdornment = (
  <InputAdornment position="start">
    <SearchIcon color="action" fontSize="small" />
  </InputAdornment>
);

const ContactsOverview = ({
  activeTab,
  blockedUsers,
  contacts,
  incomingRequests,
  loading,
  onAccept,
  onRemoveRequest,
  sentRequests,
}: ContactsOverviewProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const tabs = useMemo<TabBarItem[]>(
    () => [
      {
        count: contacts.length,
        label: intl.formatMessage({
          id: 'contacts.title',
          defaultMessage: 'Contacts',
        }),
        value: 'contacts',
      },
      {
        count: incomingRequests.length,
        label: intl.formatMessage({
          id: 'contacts.requests.incoming',
          defaultMessage: 'Incoming requests',
        }),
        value: 'incoming',
      },
      {
        count: sentRequests.length,
        label: intl.formatMessage({
          id: 'contacts.sent.title',
          defaultMessage: 'Sent requests',
        }),
        value: 'sent',
      },
      {
        count: blockedUsers.length,
        label: intl.formatMessage({
          id: 'contacts.blocked.title',
          defaultMessage: 'Blocked users',
        }),
        value: 'blocked',
      },
    ],
    [
      blockedUsers.length,
      contacts.length,
      incomingRequests.length,
      intl,
      sentRequests.length,
    ]
  );

  const filteredContacts = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();

    if (!normalizedSearch) {
      return contacts;
    }

    return contacts.filter((contact) =>
      `${getContactName(contact)} ${contact.email}`
        .toLocaleLowerCase()
        .includes(normalizedSearch)
    );
  }, [contacts, search]);

  const handleTabChange = useCallback(
    (value: string) => {
      navigate(tabPaths[value as ContactsTab]);
    },
    [navigate]
  );

  const handleCreateOpen = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    []
  );

  return (
    <Stack gap={2.5} pb={6}>
      <Breadcrumbs />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={headerStyles}>
        <Box>
          <Typography component="h1" variant="h4" sx={headingStyles}>
            {intl.formatMessage({
              id: 'contacts.title',
              defaultMessage: 'Contacts',
            })}
          </Typography>
          <Typography variant="body2" sx={descriptionStyles}>
            {intl.formatMessage({
              id: 'contacts.description',
              defaultMessage: 'People you can share sites and groups with.',
            })}
          </Typography>
        </Box>
        <Button
          onClick={handleCreateOpen}
          startIcon={<PersonAddAltOutlinedIcon />}
        >
          {intl.formatMessage({
            id: 'contacts.add',
            defaultMessage: 'Add contact',
          })}
        </Button>
      </Stack>

      {createOpen && <CreateContactCard onClose={handleCreateClose} />}

      <TabBar
        aria-label={intl.formatMessage({
          id: 'contacts.sections',
          defaultMessage: 'Contact sections',
        })}
        items={tabs}
        onChange={handleTabChange}
        value={activeTab}
      />

      {activeTab === 'contacts' && (
        <>
          <SurfaceTextField
            inputProps={{
              'aria-label': intl.formatMessage({
                id: 'contacts.search',
                defaultMessage: 'Search contacts',
              }),
            }}
            InputProps={{ startAdornment }}
            onChange={handleSearchChange}
            placeholder={intl.formatMessage({
              id: 'contacts.search',
              defaultMessage: 'Search contacts',
            })}
            surface="sunken"
            sx={searchStyles}
            value={search}
          />
          {!loading && filteredContacts.length === 0 ? (
            <Paper sx={emptyStyles}>
              <Typography color="text.secondary" variant="body2">
                {search
                  ? intl.formatMessage({
                      id: 'contacts.search.empty',
                      defaultMessage: 'No contacts match your search',
                    })
                  : intl.formatMessage({
                      id: 'contacts.empty',
                      defaultMessage: 'No contacts',
                    })}
              </Typography>
            </Paper>
          ) : (
            <ContactsTable loading={loading} rows={filteredContacts} />
          )}
        </>
      )}

      {activeTab === 'incoming' && (
        <>
          <Alert severity="info" variant="outlined">
            {intl.formatMessage({
              id: 'contacts.requests.help',
              defaultMessage:
                'After accepting, you will be able to share sites and groups with each other.',
            })}
          </Alert>
          {!loading && incomingRequests.length === 0 ? (
            <Paper sx={emptyStyles}>
              <Typography color="text.secondary" variant="body2">
                {intl.formatMessage({
                  id: 'contacts.requests.empty',
                  defaultMessage: 'No contact requests',
                })}
              </Typography>
            </Paper>
          ) : (
            <IncomingRequestsTable
              loading={loading}
              onAccept={onAccept}
              onRemove={onRemoveRequest}
              rows={incomingRequests}
            />
          )}
        </>
      )}

      {activeTab === 'sent' &&
        (!loading && sentRequests.length === 0 ? (
          <Paper sx={emptyStyles}>
            <Typography color="text.secondary" variant="body2">
              {intl.formatMessage({
                id: 'contacts.sent.empty',
                defaultMessage: 'No sent requests',
              })}
            </Typography>
          </Paper>
        ) : (
          <SentRequestsTable
            loading={loading}
            onRemove={onRemoveRequest}
            rows={sentRequests}
          />
        ))}

      {activeTab === 'blocked' &&
        (!loading && blockedUsers.length === 0 ? (
          <Paper sx={emptyStyles}>
            <Typography color="text.secondary" variant="body2">
              {intl.formatMessage({
                id: 'contacts.blocked.empty',
                defaultMessage: 'No blocked users',
              })}
            </Typography>
          </Paper>
        ) : (
          <BlockedUsersTable loading={loading} rows={blockedUsers} />
        ))}
    </Stack>
  );
};

export { ContactsOverview };
